import { ScreenSize } from '../screenDetection/screenDetection';
import { ColumnScope, ColumnSettingsStore } from './playlistColumns';

/** The key layouts above 1440px used to be saved under. */
const xLarge = ScreenSize.XLarge as string;

/**
 * Folds a stored X-Large layout into Large, once, for a user who has one.
 *
 * Large and X-Large were separate saved layouts until the columns began
 * following the window; see the note on ColumnScreenSize for why they no
 * longer are. Anyone who used the app before that has both, and a merge
 * has to pick one - so it keeps the one for the screen they are on, which
 * is the one they have been looking at, and drops the other.
 *
 * Mutates and returns whether anything changed, because the caller has to
 * persist a fold immediately: leaving the old key in place would have the
 * app choose the other layout on the next load from a different screen,
 * and the two would trade places every time the user changed monitor.
 */
export default function foldXLargeLayouts(store: ColumnSettingsStore, screenSize: ScreenSize) {
  let folded = false;

  for (const scopeKey in ColumnScope) {
    const scope = scopeKey as ColumnScope;

    // Indexed past the type on purpose: X-Large is not a key any more,
    // which is exactly why it has to be read out of old data here.
    const layouts = store[scope] as Record<string, unknown>;
    const references = store.referenceWidths[scope] as Record<string, unknown> | undefined;

    if (!layouts || layouts[xLarge] === undefined) {
      continue;
    }

    if (layouts[ScreenSize.Large] === undefined || screenSize === ScreenSize.XLarge) {
      layouts[ScreenSize.Large] = layouts[xLarge];

      // The reference travels with the pixels it describes. Where the
      // promoted layout has none, Large's own has to go: pixels from one
      // layout under another's reference would be read as a resize that
      // never happened.
      if (references) {
        if (references[xLarge] !== undefined) {
          references[ScreenSize.Large] = references[xLarge];
        } else {
          delete references[ScreenSize.Large];
        }
      }
    }

    delete layouts[xLarge];

    if (references) {
      delete references[xLarge];
    }

    folded = true;
  }

  return folded;
}
